import { LitElement, css, html, nothing } from "lit";
import { CHAT_BUBBLE_MAX_WIDTH } from "../chat-column-metrics.js";
import { customElement, property, state as litState } from "lit/decorators.js";
import { toAfasFile, type AfasAttachmentFile } from "../attachment-file.js";
import { absolutizeCopyTextLinks, normalizeArticleUrl } from "../source-url.js";
import { renderBlocks } from "../render-blocks.js";
import { requestImageDialog } from "./jonas-image-dialog.js";
import { FlipController } from "../flip.js";
import type { ChatLabels, ChatMessage } from "../types.js";

// Shadow roots that already carry the injected stylesheet (see
// ensureAfasMessageStyles). Keyed by the afas-message shadow root so the
// sheet is added exactly once per instance, even across re-renders.
const afasInjectedStyleRoots = new WeakSet<ShadowRoot>();

// How long the copy button shows the "copied" check before reverting to the copy icon.
// Matches the share-link dialog's confirmation dwell so both copy affordances feel the same.
const COPIED_RESET_MS = 2000;

/** Mirrors COMPACT_MAX_WIDTH in jonas-chat-appbar.ts: the workspace's narrow layout. */
const COMPACT_MAX_WIDTH = 760;

@customElement("help-jonas-chat-message")
export class HelpJonasChatMessage extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      margin-bottom: 24px;
    }

    :host([data-role="user"]) {
      justify-content: flex-end;
    }

    :host([data-role="user"]) .answer {
      justify-items: end;
    }

    .answer {
      display: grid;
      width: 100%;
      min-width: 0;
      justify-items: start;
    }

    .answer--assistant {
      align-items: start;
    }

    .answer--assistant:focus,
    .answer--assistant:focus-visible {
      outline: none;
      box-shadow: none;
    }

    .message {
      display: block;
      box-sizing: border-box;
      min-width: 0;
      width: fit-content;
      max-width: min(90%, ${CHAT_BUBBLE_MAX_WIDTH}px);
      overflow-wrap: anywhere;
    }

    /*
     * Slotted user identicon. afas-message's own identicon sizing/margin rules
     * only target the identicon in its shadow tree, not slotted light-DOM
     * content, so we replicate them here to match the default (28px,
     * right-aligned) appearance.
     *
     * afas-identicon renders the initials as plain inherited-font text with no
     * white-space or font-size of its own. The chat inherits a 16px font, so
     * three-letter initials (e.g. "YOU"/"VOU"/"MOI") wrap onto two lines and get
     * clipped by the 28px circle, while two-letter "IK" still fits. Pin a smaller
     * font and prevent wrapping so 1-3 uppercase letters stay on one readable line.
     */
    .user-identicon {
      --AfasIdenticon-size: 28px;
      margin-top: 10px;
      margin-left: 12px;
      font-size: 12px;
      font-weight: 600;
      white-space: nowrap;
    }

    /* Own (user) messages are right-aligned like Teams and need no identicon or author label
       (issue #126). Hide the slotted identicon and the afas-message default identicon part;
       the author is emptied via an empty name so its row collapses. */
    :host([data-role="user"]) .user-identicon,
    :host([data-role="user"]) .message::part(identicon) {
      display: none;
    }

    .message[data-feedback="positive"] {
      --content-background-color: var(--color-positive-50);
      --content-accent-color: var(--color-positive-500);
    }

    .message[data-feedback="negative"] {
      --content-background-color: var(--color-negative-50);
      --content-accent-color: var(--color-negative-500);
    }

    .stream-preview {
      position: relative;
      display: grid;
      flex: 1 1 auto;
      box-sizing: border-box;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      /* Extra room at the bottom for the hover chip, which is centred on this edge and
         would otherwise sit against whatever ends the answer. */
      padding: 16px 16px 24px;
      user-select: text;
      background: var(--content-background-color, var(--color-base-white));
      border-left: 4px solid
        var(--content-accent-color, var(--color-background-400));
      border-radius: 4px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      line-height: 1.45;
      overflow-wrap: anywhere;
    }

    .stream-preview::after {
      position: absolute;
      top: 19px;
      left: -9px;
      display: block;
      width: 0;
      height: 0;
      content: "";
      border-top: 6px solid transparent;
      border-right: 6px solid
        var(--content-accent-color, var(--color-background-400));
      border-bottom: 6px solid transparent;
    }

    /*
     * A phone has no room to spend a 40px column on an avatar, so the answer drops it too
     * and takes the width back. Its tail goes with it: it pointed at the avatar, and the
     * question already lost its own for that reason (see ensureAfasMessageStyles, which
     * also collapses the column itself -- that grid is out of reach from here). Must come
     * after the tail it switches off: a media query adds no specificity.
     */
    @media (max-width: ${COMPACT_MAX_WIDTH}px) {
      .message::part(identicon) {
        display: none;
      }

      .stream-preview::after {
        display: none;
      }
    }

    .stream-preview__content :first-child {
      margin-top: 0;
    }

    .stream-preview__content :last-child {
      margin-bottom: 0;
    }

    /*
     * Earlier answers (everything before the most recent one) collapse into a
     * short preview so a long thread stays calm. This mirrors afas-line-clamp's
     * -webkit-box clamp, but lives in this component's own shadow root so the
     * print rules below can reach it (afas-line-clamp clamps inside a
     * nested shadow root the page-level print styles cannot touch). The line
     * count is overridable via the custom property for later finetuning.
     */
    .stream-preview__content.is-clamped {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: var(--jonas-message-clamp-lines, 10);
      overflow: hidden;
    }

    .clamp-toggle {
      margin-top: 8px;
    }

    .stream-preview__content h1,
    .stream-preview__content h2,
    .stream-preview__content h3,
    .stream-preview__content h4,
    .stream-preview__content h5,
    .stream-preview__content h6 {
      margin: 16px 0 8px;
      font-weight: 600;
      line-height: 1.3;
    }

    /* em, not rem: this scale is a ratio of the answer's body text, which inherits
       --text-font-size from the page. rem would anchor it to the 16px root instead. */
    .stream-preview__content h1 {
      font-size: 1.3em;
    }

    .stream-preview__content h2 {
      font-size: 1.15em;
    }

    .stream-preview__content h3 {
      font-size: 1.05em;
    }

    .stream-preview__content h4,
    .stream-preview__content h5,
    .stream-preview__content h6 {
      font-size: 1em;
    }

    .stream-preview__content ul,
    .stream-preview__content ol {
      margin: 8px 0;
      padding-left: 20px;
    }

    .stream-preview__content li {
      margin: 2px 0;
    }

    .stream-preview__content a {
      color: var(--color-primary-600, #0a5);
      text-decoration: underline;
    }

    .stream-preview__content code {
      padding: 1px 4px;
      font-family: ui-monospace, "Cascadia Code", "Consolas", monospace;
      font-size: 0.9em;
      /* Same theme-aware foreground/background pair as the table th cells so
         inline code stays readable in both themes (light bg #e2e8f0 / text
         #334155, dark bg #262b31 / text #adbbd3). The previous background-200
         fill was the inverted end of the scale (dark slate in light theme,
         light in dark theme) with no text colour, so it rendered dark-on-dark
         and light-on-light. */
      color: var(--color-text-on-background-700);
      background: var(--color-background-700);
      border-radius: 3px;
    }

    .stream-preview__content pre {
      margin: 10px 0;
      padding: 12px;
      overflow-x: auto;
      /* Match the th cells (and inline code above) so block code shares the
         same readable surface in both themes instead of the inverted
         background-200 fill. */
      color: var(--color-text-on-background-700);
      background: var(--color-background-700);
      border-radius: 4px;
    }

    .stream-preview__content pre code {
      padding: 0;
      background: none;
    }

    .stream-preview__content blockquote {
      margin: 10px 0;
      padding: 4px 12px;
      color: var(--color-foreground-700);
      border-left: 3px solid var(--color-background-400);
    }

    .stream-preview__content table {
      width: 100%;
      margin: 10px 0;
      border-collapse: separate;
      border-spacing: 0;
      border: 1px solid var(--color-black-200);
      border-radius: 3px;
      overflow: hidden;
    }

    .stream-preview__content th,
    .stream-preview__content td {
      padding: 8px;
      text-align: left;
      vertical-align: top;
    }

    .stream-preview__content th {
      font-weight: 600;
      /* Same theme-aware foreground/background pair as the source pill so the
         header row flips correctly in dark mode (bg #262b31, text #adbbd3)
         instead of staying a fixed light fill. */
      color: var(--color-text-on-background-700);
      background-color: var(--color-background-700);
    }

    .stream-preview__content tbody td {
      /* Subtle body fill one step softer than the header (background-700) so
         the table content reads as a distinct block against the message
         background in both themes (light #f1f5f9, dark #202325). */
      background-color: var(--color-background-900);
      border-top: 1px solid var(--color-black-200);
    }

    .stream-preview__content hr {
      margin: 14px 0;
      border: none;
      border-top: 1px solid var(--color-background-300, rgba(0, 0, 0, 0.12));
    }

    /* The per-message controls (feedback + copy) FLOAT over the bubble instead
       of taking their own row at the bottom, so the bubble height tracks the
       answer only. Vertically the chip is centered ON the bubble's bottom line
       (bottom: 0 + translateY(50%)). Horizontally it sits at a FIXED offset from
       the answer column's left edge (its right edge anchored via translateX(-100%)),
       so it keeps the same spot relative to the message and no longer drifts when a
       side panel opens/closes and reflows the column width (issue #139 — the old
       width-percentage anchor moved between panel states). A border + surface fill
       keep it legible where it overlaps content; revealed on hover via the shared
       .buttons opacity rules below. */
    .stream-preview .buttons {
      position: absolute;
      bottom: 0;
      left: 195px;
      z-index: 1;
      gap: 2px;
      margin: 0;
      background: var(--content-background-color, var(--color-base-white));
      border: 1px solid var(--color-background-400);
      border-radius: 8px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
      transform: translate(-100%, 50%);
    }

    /* Generic per-line reveal: each new list item or table row fades and slides in
       with a soft shadow that settles to crisp, echoing the "Antwoord voorbereiden"
       feel. The .is-streaming gate is the single switch for every enter animation,
       so resumed history (pending = false) never animates. A swirl/shimmer variant
       can later hook onto the same gate. Tunable via custom properties. */
    .stream-preview.is-streaming .jonas-line--list {
      animation: jonas-line-reveal var(--jonas-reveal-duration, 220ms) ease-out
        both;
    }

    .stream-preview.is-streaming .jonas-line--cell {
      animation: jonas-cell-reveal var(--jonas-reveal-duration, 220ms) ease-out
        both;
    }

    /* The FLIP controller scales the <td>; this inner wrapper counter-scales so
       cell text is never stretched while a column settles to its new width. */
    .jonas-cell {
      display: block;
      transform-origin: left center;
    }

    @keyframes jonas-line-reveal {
      from {
        opacity: 0;
        transform: translateY(-6px);
        text-shadow: 0 0 8px var(--jonas-stream-shadow, rgba(10, 90, 200, 0.35));
      }
      to {
        opacity: 1;
        transform: translateY(0);
        text-shadow: 0 0 0 transparent;
      }
    }

    @keyframes jonas-cell-reveal {
      from {
        opacity: 0;
        text-shadow: 0 0 8px var(--jonas-stream-shadow, rgba(10, 90, 200, 0.35));
      }
      to {
        opacity: 1;
        text-shadow: 0 0 0 transparent;
      }
    }

    .preparing {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 4px 0;
      color: var(--color-foreground-700);
      line-height: 1.4;
    }

    .preparing__spinner {
      flex: 0 0 auto;
    }

    .preparing__text {
      background-image: linear-gradient(
        100deg,
        var(--color-foreground-500) 0%,
        var(--color-foreground-500) 35%,
        var(--color-foreground-800) 50%,
        var(--color-foreground-500) 65%,
        var(--color-foreground-500) 100%
      );
      background-size: 200% 100%;
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
      animation: jonas-preparing-shimmer 1.8s linear infinite;
    }

    @keyframes jonas-preparing-shimmer {
      from {
        background-position: 100% 0;
      }
      to {
        background-position: -100% 0;
      }
    }

    .answer-status {
      display: inline-flex;
      align-items: center;
      justify-self: start;
      gap: 8px;
      margin-top: 12px;
      line-height: 1.4;
    }

    .answer-status--resuming {
      gap: 10px;
      color: var(--color-foreground-700);
    }

    .answer-status--incomplete {
      color: var(--color-foreground-600);
    }

    .answer-status--canceled {
      color: var(--color-warning-700);
      font-weight: 500;
    }

    .stream-preview--canceled {
      --content-accent-color: var(--color-warning-600);
      --content-background-color: var(--color-warning-50);
    }

    /* The standalone cancellation block (no streamed answer above it) carries the
       notice as its only content, so drop the separating top margin it would
       otherwise need under a partial answer. */
    .stream-preview--canceled .answer-status--canceled {
      margin-top: 0;
    }

    .answer-status__spinner {
      flex: 0 0 auto;
    }

    .answer-status__icon {
      flex: 0 0 auto;
      color: var(--color-foreground-500);
    }

    .answer-status--resuming .answer-status__text {
      background-image: linear-gradient(
        100deg,
        var(--color-foreground-500) 0%,
        var(--color-foreground-500) 35%,
        var(--color-foreground-800) 50%,
        var(--color-foreground-500) 65%,
        var(--color-foreground-500) 100%
      );
      background-size: 200% 100%;
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
      -webkit-text-fill-color: transparent;
      animation: jonas-preparing-shimmer 1.8s linear infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      .preparing__text {
        animation: none;
        background-image: none;
        color: var(--color-foreground-700);
        -webkit-text-fill-color: var(--color-foreground-700);
      }

      .answer-status--resuming .answer-status__text {
        animation: none;
        background-image: none;
        color: var(--color-foreground-700);
        -webkit-text-fill-color: var(--color-foreground-700);
      }

      .stream-preview.is-streaming .jonas-line--list,
      .stream-preview.is-streaming .jonas-line--cell {
        animation: none;
      }
    }

    .user-attachments {
      min-width: 0;
      max-width: 100%;
    }

    .user-attachments afas-file {
      width: min(100%, 256px);
    }

    /* A snapshot-restored attachment has only metadata (no base64 data), so it renders
       as a non-previewable chip; this note explains the missing preview so the chip
       does not read as broken. */
    .attachment-unavailable {
      display: block;
      margin-top: 4px;
      color: var(--color-foreground-600);
    }

    .buttons {
      display: flex;
      gap: 8px;
      margin-left: 56px;
      /* Pin the row to the action buttons' own height and center them. When the
         copy button flips to its "done" state, the AFAS button swaps in a larger
         (medium) CheckOutlined icon that briefly makes its tooltip wrapper taller;
         without this the flex row (and the floating .stream-preview popover) would
         stretch ~6px taller for the copied window. The check icon is absolutely
         positioned and centered, so it stays fully visible within this height. */
      align-items: center;
      height: 32px;
      transition:
        opacity 160ms ease,
        visibility 160ms ease;
      /* The popover only reveals after a 500ms hover dwell so a quick pass over an answer
         does not flash it; hiding stays immediate (transition-delay: 0s in the rules below). */
      transition-delay: 500ms;
    }

    @media (hover: hover) and (pointer: fine) {
      .message:not(:hover):not(:focus-within) .buttons {
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition-delay: 0s;
      }

      /* A rating click dismisses the popover at once (even while still hovering); the class
         clears on pointerleave so the next hover reveals it again after the delay. */
      .answer.actions-dismissed .buttons {
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition-delay: 0s;
      }
    }

    @media (hover: none), (pointer: coarse) {
      .buttons {
        opacity: 1;
        visibility: visible;
        pointer-events: auto;
      }
    }

    /*
     * Print (Ctrl+P): the per-message controls (copy, feedback) are interactive
     * affordances with no meaning on paper. Drop them so only the message
     * content and its sources print. The thread is moved into the print root, so
     * these shadow-scoped rules apply there too.
     *
     * A single answer can be taller than one printed page. A display:flex/grid
     * container taller than a page does NOT fragment across pages in Chromium —
     * it clips to the first page, silently dropping the rest of the answer. So
     * every flex/grid box in the message's own layout switches to block for
     * paper, letting a long answer paginate cleanly. (afas-message wraps the
     * content in its OWN shadow grid, which we cannot reach from here; that grid
     * is converted to block by a sheet injected in ensureAfasMessageStyles.)
     */
    @media print {
      :host {
        display: block;
      }

      /*
       * The avatar/identicon is redundant with the author name on paper: the
       * user identicon ("IK") duplicates the "Ik" label, and the Jonas avatar
       * sits above the "Jonas" label. Hide both — the slotted user identicon and
       * afas-message's own identicon (exposed as ::part(identicon)) — keeping the
       * author name.
       */
      .user-identicon {
        display: none;
      }

      afas-message::part(identicon) {
        display: none;
      }

      .answer,
      .answer--assistant {
        display: block;
      }

      .stream-preview {
        display: block;
      }

      /*
       * Never clamp an earlier answer on paper — a printout must contain the
       * full conversation. Unset the -webkit-box clamp and drop the "Toon meer"
       * toggle, which has no meaning in print.
       */
      .stream-preview__content.is-clamped {
        display: block;
        overflow: visible;
        -webkit-line-clamp: unset;
      }

      .clamp-toggle {
        display: none !important;
      }

      .buttons {
        display: none !important;
      }

      /*
       * Keep wide block content within the printed page width. On screen a long
       * code line or wide table is contained by overflow:auto/hidden, but paper
       * has no scrollbars: Chromium expands those boxes to reveal all content,
       * so a 1900px <pre> line (or an auto-layout table) grows the print canvas
       * far wider than the page. Chromium then scales the WHOLE (tall) thread
       * down to fit that width onto a SINGLE page, clipping everything below the
       * fold (the "1/1" symptom) and pushing right-aligned questions to the far
       * edge. Forcing wrap/containment keeps the canvas at page width so the
       * answer paginates by height instead.
       */
      .stream-preview__content pre {
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        word-break: break-word;
      }

      .stream-preview__content table {
        table-layout: fixed;
      }

      .stream-preview__content th,
      .stream-preview__content td {
        overflow-wrap: anywhere;
        word-break: break-word;
      }

      .stream-preview__content img,
      .stream-preview__content svg,
      .stream-preview__content video {
        max-width: 100%;
        height: auto;
      }
    }
  `;

  @property({ attribute: false })
  accessor message: ChatMessage | null = null;

  @property({ attribute: false })
  accessor labels: ChatLabels | null = null;

  // Set by the thread for every answer except the most recent one: an earlier
  // answer renders as a clamped preview with a "Toon meer" toggle.
  @property({ attribute: false })
  accessor clamped = false;

  // True while this message's feedback vote is being sent (optimistically applied). The
  // thumbs render disabled so a double-click cannot fire a second request. Driven by the
  // store's feedbackPendingId via the thread.
  @property({ attribute: false })
  accessor feedbackPending = false;

  @litState()
  private accessor copiedMessageId = "";

  // A rating click force-hides the actions popover at once (even while still hovering) so
  // the rating feels instant; it clears on pointerleave so the next hover reveals it again.
  @litState()
  private accessor actionsDismissed = false;

  // User pressed "Toon meer" on this (clamped) answer. State lives on the
  // component instance, which Lit reuses by list position, so an expanded
  // earlier answer stays expanded across later turns.
  @litState()
  private accessor expanded = false;

  // The clamped content actually overflows the preview height, so the toggle is
  // worth showing. Sticky once true while the message stays clamp-eligible.
  @litState()
  private accessor isTruncated = false;

  // The clamped content is projected through afas-message's shadow slot, which
  // lays out asynchronously after our first render. A ResizeObserver re-measures
  // truncation once that layout settles (height 0 -> real), so the toggle shows
  // even though no Lit update is triggered by the slot rendering.
  private contentResizeObserver: ResizeObserver | null = null;

  private observedContentEl: HTMLElement | null = null;

  private truncationFrame = 0;

  // Reverts the copy button's "copied" check icon back to the copy icon after COPIED_RESET_MS.
  private copiedResetTimer = 0;

  // Drives the interval that keeps the forced "Gekopieerd" tooltip open for the copied window.
  private copiedTooltipTimer = 0;

  // Runs a FLIP pass over table cells each render so columns that re-flow when new
  // rows stream in settle smoothly instead of snapping to a new width.
  private readonly flip = new FlipController(this);

  protected override updated(): void {
    if (this.message) this.setAttribute("data-role", this.message.role);
    this.ensureAfasMessageStyles();
    this.ensureContentResizeObserver();
    this.measureTruncation();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.contentResizeObserver?.disconnect();
    this.contentResizeObserver = null;
    this.observedContentEl = null;
    if (this.truncationFrame) {
      cancelAnimationFrame(this.truncationFrame);
      this.truncationFrame = 0;
    }
    window.clearTimeout(this.copiedResetTimer);
    window.clearInterval(this.copiedTooltipTimer);
  }

  private getContentElement(): HTMLElement | null {
    return (
      this.shadowRoot?.querySelector<HTMLElement>(".stream-preview__content") ??
      null
    );
  }

  // Keeps a ResizeObserver pinned to the current content element so a late
  // layout (afas-message projecting our content) or a width change re-runs the
  // truncation check. measureTruncation only flips a boolean and the observed
  // element is not resized by it, so there is no observer feedback loop.
  private ensureContentResizeObserver(): void {
    if (typeof ResizeObserver === "undefined") return;
    const el = this.getContentElement();
    if (el === this.observedContentEl) return;
    if (!this.contentResizeObserver) {
      this.contentResizeObserver = new ResizeObserver(() => {
        if (this.truncationFrame) return;
        this.truncationFrame = requestAnimationFrame(() => {
          this.truncationFrame = 0;
          this.measureTruncation();
        });
      });
    }
    if (this.observedContentEl) {
      this.contentResizeObserver.unobserve(this.observedContentEl);
    }
    this.observedContentEl = el;
    if (el) this.contentResizeObserver.observe(el);
  }

  // Detects whether the clamped preview hides part of the answer (so the "Toon
  // meer" toggle is warranted). Skipped while expanded (content is shown in full,
  // so keep the last known value).
  private measureTruncation(): void {
    if (!this.clamped) {
      this.isTruncated = false;
      return;
    }
    if (this.expanded) return;
    const el = this.getContentElement();
    if (!el) return;
    this.isTruncated = el.scrollHeight > el.clientHeight + 1;
  }


  /*
   * afas-message lays its content out inside its OWN shadow root with two boxes
   * that are taller-than-a-page hazards for print:
   *   1. `.message` — the outer CSS grid (identicon / author / content / alt …).
   *   2. `slot[name="alt"]` (and `slot[name="bottom"]`) — flex boxes that project
   *      our assistant answer (`<div slot="alt" class="stream-preview">`).
   * A flex OR grid box taller than one printed page is monolithic in the
   * INTERACTIVE print preview: it does NOT fragment, so it clips to the first
   * page and silently drops the rest of a long answer. (Headless
   * Page.printToPDF DOES fragment them, which is why the clip only reproduces
   * via Ctrl+P / "Save as PDF" — verified by CDP: the alt slot was a single
   * 2730px-tall display:flex box while printToPDF still emitted 4 pages.)
   * We cannot reach these boxes from this component's stylesheet (afas-message
   * only exposes the identicon as a ::part), so inject a print-only sheet into
   * the afas-message shadow root that switches the grid AND the flex content
   * slots to block layout for paper. The sheet is gated by @media print, so it
   * never changes on-screen rendering, and it is added once per shadow root.
   *
   * The same sheet drops the speech-bubble tail on the visitor's question. It is
   * drawn as a CSS triangle on `.content::after` out of --content-accent-color,
   * the same variable as the bubble's accent border, so it cannot be turned off
   * with a custom property. It pointed at the identicon we no longer show.
   *
   * And on a narrow viewport it collapses the identicon column, which the styles
   * above empty but cannot resize: afas-message 2.1.23 has no property for an
   * avatar-less message. `is-quote` is the only one that drops the identicon, and
   * it repaints the whole message as a grey quote block — verified in the browser,
   * where combined with has-alt it also left a broken four-column grid.
   */
  private ensureAfasMessageStyles(): void {
    const afasMessage = this.shadowRoot?.querySelector("afas-message");
    const afasRoot = afasMessage?.shadowRoot;
    if (!afasRoot || afasInjectedStyleRoots.has(afasRoot)) return;
    if (
      typeof CSSStyleSheet === "undefined" ||
      !("adoptedStyleSheets" in afasRoot)
    ) {
      return;
    }
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(
        `.message .content::after { display: none; }
        @media (max-width: ${COMPACT_MAX_WIDTH}px) {
          .message { grid-template-columns: 0 1fr 1fr; }
          .message.align-right { grid-template-columns: 1fr 1fr 0; }
          .author { padding-left: 0; }
        }
        @media print {
          .message { display: block !important; }
          slot[name="alt"], slot[name="bottom"] { display: block !important; }
        }`,
      );
      afasRoot.adoptedStyleSheets = [...afasRoot.adoptedStyleSheets, sheet];
      afasInjectedStyleRoots.add(afasRoot);
    } catch {
      // Some third-party shadow roots may reject adopted sheets; the question then
      // keeps its tail and printing falls back to the (clipped) grid.
    }
  }

  protected override render() {
    const message = this.message;
    if (!message) return null;

    return html`
      <div
        class=${this.getAnswerClass(message)}
        tabindex=${this.getAnswerTabIndex(message)}
      >
        <afas-message
          class="message"
          role="article"
          aria-busy=${message.pending && !message.finalizing ? "true" : "false"}
          aria-label=${this.getMessageAriaLabel(message)}
          name=${this.getAuthorName(message)}
          .message=${this.getAfasMessageText(message)}
          .date=${this.getMessageDate(message)}
          ?has-markdown=${this.shouldRenderAfasMarkdown(message)}
          ?has-alt=${this.shouldRenderAlt(message)}
          identicon-align=${this.getIdenticonAlign(message)}
          data-feedback=${message.feedback || nothing}
          @click=${this.handleAnswerClick}
          @pointerleave=${this.handleActionsPointerLeave}
        >
          ${message.role === "user"
            ? html`<afas-identicon
                  slot="identicon"
                  class="user-identicon"
                  name=${this.getUserIdenticonName(message)}
                ></afas-identicon>
                ${this.renderUserAttachments(message)}`
            : this.renderAssistantContent(message)}
        </afas-message>
      </div>
    `;
  }

  private renderAssistantContent(message: ChatMessage) {
    if (this.shouldRenderAssistantBlocks(message)) {
      return this.renderAssistantBlocks(message);
    }

    if (this.isPreparing(message)) {
      return this.renderPreparingIndicator();
    }

    if (message.canceled) {
      return this.renderCanceledBlock(message);
    }

    return html`${this.renderMessageActions(message)}`;
  }

  private renderPreparingIndicator() {
    const label = this.getLabel("preparingAnswer", "Antwoord voorbereiden");
    return html`<div
      slot="alt"
      class="preparing"
      role="status"
      aria-live="polite"
    >
      <afas-loading-spinner
        class="preparing__spinner"
        size="small"
      ></afas-loading-spinner>
      <span class="preparing__text">${label}</span>
    </div>`;
  }

  private renderUserAttachments(message: ChatMessage) {
    if (message.role !== "user" || !message.attachments?.length) return null;

    const afasFiles = message.attachments.map((attachment, index) =>
      toAfasFile(attachment, index, message.id),
    );
    // A snapshot-restored attachment has metadata but no base64 data (dropped for the
    // sessionStorage quota), so it cannot be previewed. Surface that once so the chip
    // does not look broken; freshly-uploaded attachments keep their preview.
    const hasUnavailable = afasFiles.some((file) => !file.previewUrl);

    return html`<afas-file-group
      slot="attachments"
      class="user-attachments"
      aria-label=${this.getLabel("attachmentsLabel", "Bijlagen")}
      display="grid"
      .collapsible=${false}
    >
      ${afasFiles.map(
        (afasFile) =>
          html`<afas-file
            readonly
            tone="neutral"
            .file=${afasFile}
            .viewEnabled=${Boolean(afasFile.previewUrl)}
            .removeEnabled=${false}
            .replaceEnabled=${false}
            .imageEditEnabled=${false}
            @preview=${this.handleAttachmentPreview}
          ></afas-file>`,
      )}
    </afas-file-group>
    ${hasUnavailable
      ? html`<span slot="attachments" class="attachment-unavailable">
          ${this.getLabel(
            "attachmentUnavailable",
            "Afbeelding niet meer beschikbaar.",
          )}
        </span>`
      : null}`;
  }

  private handleAttachmentPreview(
    event: CustomEvent<AfasAttachmentFile>,
  ): void {
    const file = event.detail;
    const src = file?.previewUrl;
    if (!src) return;
    // Open the attachment in the shared app-root image dialog (see
    // jonas-image-dialog); the request bubbles up to the app frame.
    requestImageDialog(this, {
      src,
      alt: file.filename,
      title: file.filename,
    });
  }

  private renderFeedback(message: ChatMessage) {
    // Show the thumbs as soon as the answer is settled: during finalizing they appear
    // disabled (the server only confirms canGiveFeedback at completion), so the action
    // menu looks complete and does not reflow when feedback becomes available. Stays
    // hidden for a fallback message that completes without feedback (canGiveFeedback false).
    if (
      message.role !== "assistant" ||
      (!message.finalizing && !message.canGiveFeedback)
    ) {
      return null;
    }
    const goodLabel = this.getLabel("feedbackGood", "Goede reactie");
    const badLabel = this.getLabel("feedbackBad", "Slechte reactie");
    const positiveSelected = message.feedback === "positive";
    const negativeSelected = message.feedback === "negative";
    // Thumbs stay enabled after a vote so it can be switched (👍↔👎) or retracted to neutral,
    // but disable while a vote is in flight (feedbackPending) so a double-click cannot fire a
    // second request; a not-yet-rateable answer (finalizing) also disables them.
    const disabled = !message.canGiveFeedback || this.feedbackPending;
    return html`
      <afas-button
        variant="minimal"
        tone="neutral"
        icon-only
        label=${goodLabel}
        ?data-selected=${positiveSelected}
        ?disabled=${disabled}
        aria-pressed=${positiveSelected ? "true" : "false"}
        aria-disabled=${disabled ? "true" : "false"}
        @click=${() => this.feedback("positive")}
      >
        <afas-icon slot="icon" icon="ThumbUpFilled"></afas-icon>
      </afas-button>
      <afas-button
        variant="minimal"
        tone="neutral"
        icon-only
        label=${badLabel}
        ?data-selected=${negativeSelected}
        ?disabled=${disabled}
        aria-pressed=${negativeSelected ? "true" : "false"}
        aria-disabled=${disabled ? "true" : "false"}
        @click=${() => this.feedback("negative")}
      >
        <afas-icon slot="icon" icon="ThumbDownFilled"></afas-icon>
      </afas-button>
    `;
  }

  private renderMessageActions(message: ChatMessage, useBottomSlot = true) {
    // A user-canceled turn shows the cancellation notice instead of feedback/copy.
    if (message.canceled) return null;
    // While finalizing the visible answer is settled, so the copy action (and the
    // disclaimer in the thread) appear early; feedback stays gated on canGiveFeedback,
    // which the server only sets at completion.
    if (message.role !== "assistant" || (message.pending && !message.finalizing)) {
      return null;
    }
    const copyLabel = this.getLabel("copyMessage", "Kopieer bericht");
    const copiedLabel = this.getLabel(
      "copyMessageCopied",
      "Bericht gekopieerd",
    );
    const copied = this.copiedMessageId === message.id;
    const shareLabel = this.getLabel("shareMessage", "Gesprek delen");
    const buttons = html`
      ${this.renderFeedback(message)}
      <afas-tooltip
        class="copy-tooltip"
        content=${copied ? copiedLabel : copyLabel}
      >
        <afas-button
          variant="minimal"
          tone=${copied ? "positive" : "neutral"}
          icon-only
          state=${copied ? "done" : "none"}
          label=${copied ? copiedLabel : copyLabel}
          @click=${() => this.copyMessage(message)}
        >
          <afas-icon slot="icon" icon="ContentCopyOutlined"></afas-icon>
        </afas-button>
      </afas-tooltip>
      <afas-button
        variant="minimal"
        tone="neutral"
        icon-only
        label=${shareLabel}
        @click=${() => this.share(message)}
      >
        <afas-icon slot="icon" icon="LinkOutlined"></afas-icon>
      </afas-button>
    `;
    return useBottomSlot
      ? html`<div class="buttons" slot="bottom">${buttons}</div>`
      : html`<div class="buttons">${buttons}</div>`;
  }

  private renderAssistantBlocks(message: ChatMessage) {
    if (!this.shouldRenderAssistantBlocks(message)) return null;
    // is-streaming is the single gate for every enter animation. It is dropped once the
    // message is finalizing (visible answer settled) so the settled prose renders still
    // while the follow-ups finish, and resumed history renders crisp and still.
    const streamPreviewClass =
      message.pending && !message.finalizing
        ? "stream-preview is-streaming"
        : "stream-preview";
    // Clamp earlier answers into a preview, unless the user expanded this one.
    const clampActive = this.clamped && !this.expanded;
    const contentClass = clampActive
      ? "stream-preview__content is-clamped"
      : "stream-preview__content";
    return html`<div slot="alt" class=${streamPreviewClass}>
      <div class=${contentClass}>
        ${renderBlocks(message.blocks, message.sources, this.labels)}
      </div>
      ${this.renderClampToggle()}
      ${this.renderIncompleteNotice(message)}
      ${this.renderCanceledNotice(message)}
      ${this.renderMessageActions(message, false)}
    </div>`;
  }

  // The "Toon meer" / "Toon minder" affordance for a clamped earlier answer. It
  // mirrors afas-line-clamp's toggle button (minimal neutral button + chevron)
  // and only appears once the clamp actually hides content.
  private renderClampToggle() {
    if (!this.clamped || !this.isTruncated) {
      return null;
    }
    const label = this.expanded
      ? this.getLabel("showLess", "Toon minder")
      : this.getLabel("showMore", "Toon meer");
    return html`<afas-button
      class="clamp-toggle"
      label=${label}
      tone="neutral"
      variant="minimal"
      size="small"
      @click=${this.toggleExpanded}
    >
      <afas-icon
        slot="icon"
        icon=${this.expanded
          ? "KeyboardArrowUpOutlined"
          : "KeyboardArrowDownOutlined"}
      ></afas-icon>
    </afas-button>`;
  }

  private toggleExpanded(event: Event): void {
    event.stopPropagation();
    this.expanded = !this.expanded;
  }


  private renderIncompleteNotice(message: ChatMessage) {
    if (message.role !== "assistant" || !message.incomplete) return null;
    // The streamed partial of an interrupted answer. While it is being recovered the
    // resume poll keeps it pending, so show a "wordt voltooid" indicator; once the
    // poll gives up it is no longer pending, so show a subtle "mogelijk onvolledig"
    // notice. The full answer replaces the partial in place when it lands.
    if (message.pending) {
      const label = this.getLabel("resumingAnswer", "Antwoord wordt voltooid…");
      return html`<div
        class="answer-status answer-status--resuming"
        role="status"
        aria-live="polite"
      >
        <afas-loading-spinner
          class="answer-status__spinner"
          size="small"
        ></afas-loading-spinner>
        <span class="answer-status__text">${label}</span>
      </div>`;
    }
    const label = this.getLabel(
      "incompleteAnswer",
      "Dit antwoord is mogelijk onvolledig. Stel je vraag opnieuw voor een volledig antwoord.",
    );
    return html`<div
      class="answer-status answer-status--incomplete"
      role="note"
    >
      <afas-icon class="answer-status__icon" icon="InfoOutlined"></afas-icon>
      <span class="answer-status__text">${label}</span>
    </div>`;
  }

  // The empty assistant bubble of a user-stopped turn: render the cancellation notice
  // inside a response block (warning accent) so it reads as a normal answer block whose
  // content is "the answer was stopped", not an empty bubble.
  private renderCanceledBlock(message: ChatMessage) {
    return html`<div slot="alt" class="stream-preview stream-preview--canceled">
      <div class="stream-preview__content">
        ${this.renderCanceledNotice(message)}
      </div>
    </div>`;
  }

  // The localized "answer stopped" line shown for a user-canceled turn. Rendered on its
  // own for an empty turn (renderCanceledBlock) or appended under a partially streamed
  // answer (renderAssistantBlocks). Never paired with feedback/copy actions.
  private renderCanceledNotice(message: ChatMessage) {
    if (!message.canceled) return null;
    const label = this.getLabel("stoppedMessage", "Het antwoord is gestopt.");
    return html`<div class="answer-status answer-status--canceled" role="note">
      <span class="answer-status__text">${label}</span>
    </div>`;
  }

  private openSource(url: string, title: string): void {
    this.dispatchEvent(
      new CustomEvent("jonas-source-open", {
        detail: { url, title },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleAnswerClick(event: MouseEvent): void {
    const anchor = findAnchor(event);
    if (anchor && shouldConsiderLink(event, anchor)) {
      const href = anchor.getAttribute("href") ?? "";
      const articleUrl = normalizeArticleUrl(href, window.location.href);
      if (!articleUrl) return;

      event.preventDefault();
      this.openSource(articleUrl.path, getLinkTitle(anchor, articleUrl.path));
      return;
    }

    // Click anywhere on a clamped preview (other than a link or the toggle
    // button) expands it, mirroring afas-line-clamp's click-to-expand.
    if (this.clamped && !this.expanded && this.isTruncated) {
      const target = event.target as HTMLElement | null;
      if (target && !target.closest("a, afas-button, button")) {
        this.expanded = true;
      }
    }
  }

  private feedback(value: "positive" | "negative"): void {
    // Guard against a stray activation while the thumbs are disabled (finalizing before the
    // server confirms feedback, or while a previous vote is still being sent).
    if (!this.message || !this.message.canGiveFeedback || this.feedbackPending)
      return;
    // Hide the actions popover at once so the rating feels instant and the user is not
    // tempted to click again; it reappears on the next hover (after the reveal delay).
    this.actionsDismissed = true;
    // Toggle: clicking the already-selected thumb retracts the vote (null = neutral);
    // clicking the other thumb switches. The platform overwrites/deletes this evaluator's
    // feedback, so a mistaken rating can always be corrected.
    const next: "positive" | "negative" | null =
      this.message.feedback === value ? null : value;
    this.dispatchEvent(
      new CustomEvent("jonas-feedback-submit", {
        detail: { messageId: this.message.id, value: next },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private share(message: ChatMessage): void {
    this.dispatchEvent(
      new CustomEvent("jonas-share-request", {
        detail: { messageId: message.id },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private async copyMessage(message: ChatMessage): Promise<void> {
    // message.content is the server-provided copy text (markdown): the platform
    // answer with the machine-readable metadata blocks removed and each inline
    // source pill rendered as a [label](<url>) markdown link. Copying it directly
    // keeps a single source of truth (the server) for the markdown, so the frontend
    // does not re-serialize the parsed block model. Its internal links are relative,
    // so absolutize them against this page before they leave the app.
    const text = absolutizeCopyTextLinks(
      (message.content ?? "").trim(),
      window.location.href,
    );
    if (!text) return;
    // Show the forced "Gekopieerd" tooltip immediately and re-arm it on every (repeated) click,
    // overriding the afas-tooltip's own hover/mousedown hide. The confirmation shows even if the
    // clipboard write is blocked, and reverts together with the check icon after COPIED_RESET_MS.
    this.copiedMessageId = message.id;
    this.startForcingCopiedTooltip();
    window.clearTimeout(this.copiedResetTimer);
    this.copiedResetTimer = window.setTimeout(() => {
      if (this.copiedMessageId === message.id) {
        this.copiedMessageId = "";
        this.stopForcingCopiedTooltip();
      }
    }, COPIED_RESET_MS);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard can be blocked (permissions/insecure context); the confirmation still shows.
    }
  }

  // The afas-tooltip hides itself on mousedown/mouseleave, so a plain `active` binding does not
  // survive a copy click (or repeated clicks). Re-assert `active` each frame while copied so the
  // "Gekopieerd" tooltip stays forced open until the check icon reverts.
  private setCopyTooltipActive(active: boolean): void {
    this.renderRoot
      ?.querySelectorAll<HTMLElement & { active?: boolean }>(".copy-tooltip")
      .forEach((tip) => {
        tip.active = active;
      });
  }

  private startForcingCopiedTooltip(): void {
    window.clearInterval(this.copiedTooltipTimer);
    this.setCopyTooltipActive(true);
    // A rAF loop is throttled in background/occluded tabs; a short interval re-asserts the forced
    // tooltip reliably so it survives the tooltip's own mousedown/mouseleave hide until the
    // copied window ends.
    this.copiedTooltipTimer = window.setInterval(() => {
      if (!this.copiedMessageId) {
        window.clearInterval(this.copiedTooltipTimer);
        return;
      }
      this.setCopyTooltipActive(true);
    }, 100);
  }

  private stopForcingCopiedTooltip(): void {
    window.clearInterval(this.copiedTooltipTimer);
    this.copiedTooltipTimer = 0;
    this.setCopyTooltipActive(false);
  }

  private getLabel(key: string, fallback: string): string {
    const value = this.labels?.[key]?.trim();
    return value || fallback;
  }

  private handleActionsPointerLeave(): void {
    // Re-arm the actions popover once the pointer leaves the message, so a future hover
    // shows it again after a rating click dismissed it.
    if (this.actionsDismissed) this.actionsDismissed = false;
  }

  private getAnswerClass(message: ChatMessage): string {
    const base = `answer answer--${message.role}`;
    // actions-dismissed force-hides the popover even under :hover (set on a rating click);
    // it clears on pointerleave so the next hover reveals it again.
    return this.actionsDismissed ? `${base} actions-dismissed` : base;
  }

  private getAnswerTabIndex(message: ChatMessage): string | undefined {
    if (message.role !== "assistant" || message.pending) return undefined;
    return "0";
  }

  private getAfasMessageText(message: ChatMessage): string {
    // Assistant answers render exclusively through parsed blocks
    // (renderAssistantBlocks) or the preparing indicator. The raw markdown
    // content must never surface as plain text: while streaming, the parsed
    // blocks are briefly empty (e.g. a leading "## " heading before its text
    // arrives, which slices to zero revealed characters), which would otherwise
    // flash the literal markdown markers. User messages pass their content here
    // and afas-message renders it as styled markdown (see shouldRenderAfasMarkdown).
    if (message.role === "assistant") return "";
    return message.content || "";
  }

  private shouldRenderAfasMarkdown(message: ChatMessage): boolean {
    // User questions are authored in the markdown-aware composer
    // (afas-comment-form), so render them through afas-message's has-markdown,
    // which uses the same AFAS afas-markdown engine as the composer: one markdown
    // flavor for both input and display, with no streaming/block model needed.
    // The content is complete on send (never streamed), so there is no raw-marker
    // flash risk that keeps assistant answers on the parsed-block path.
    return message.role === "user";
  }

  private shouldRenderAssistantBlocks(message: ChatMessage): boolean {
    return message.role === "assistant" && message.blocks.length > 0;
  }

  private shouldRenderAlt(message: ChatMessage): boolean {
    return (
      this.shouldRenderAssistantBlocks(message) ||
      this.isPreparing(message) ||
      message.canceled === true
    );
  }

  private isPreparing(message: ChatMessage): boolean {
    return (
      message.role === "assistant" &&
      message.pending &&
      !this.shouldRenderAssistantBlocks(message)
    );
  }

  private getMessageDate(message: ChatMessage): Date | undefined {
    const date = new Date(message.createdUtc);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  private getAuthorName(message: ChatMessage): string {
    // Own (user) messages are right-aligned (like Teams) and show no author label; the
    // assistant keeps its name. Accessibility is covered by the message aria-label.
    return message.role === "assistant" ? "AfasJonas" : "";
  }

  // The default afas-identicon derives its initials from the author name and
  // only keeps uppercase letters (e.g. "Ik" -> "I"). Uppercasing the label so
  // the slotted user identicon shows both letters (e.g. "IK"/"ME"/"MOI").
  private getUserIdenticonName(message: ChatMessage): string {
    return this.getAuthorName(message).toUpperCase();
  }

  private getIdenticonAlign(message: ChatMessage): "left" | "right" {
    return message.role === "user" ? "right" : "left";
  }

  private getMessageAriaLabel(message: ChatMessage): string {
    return message.role === "assistant"
      ? this.getLabel("assistantMessageAriaLabel", "Antwoord")
      : this.getLabel("userMessageAriaLabel", "Vraag");
  }
}

function findAnchor(event: MouseEvent): HTMLAnchorElement | null {
  return (
    event
      .composedPath()
      .find(
        (target): target is HTMLAnchorElement =>
          target instanceof HTMLAnchorElement,
      ) ?? null
  );
}

function shouldConsiderLink(
  event: MouseEvent,
  anchor: HTMLAnchorElement,
): boolean {
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
    return false;

  const target = anchor.target.trim().toLowerCase();
  if (target && target !== "_self" && target !== "_blank") return false;

  const href = (anchor.getAttribute("href") ?? "").trim();
  if (!href || href.startsWith("#")) return false;

  const lowerHref = href.toLowerCase();
  return !(
    lowerHref.startsWith("mailto:") ||
    lowerHref.startsWith("tel:") ||
    lowerHref.startsWith("javascript:") ||
    lowerHref.startsWith("data:") ||
    lowerHref.startsWith("vbscript:")
  );
}

function getLinkTitle(anchor: HTMLAnchorElement, fallback: string): string {
  return anchor.textContent?.trim() || anchor.title.trim() || fallback;
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-chat-message": HelpJonasChatMessage;
  }
}
